import { useState } from 'react';
import { useFormContext, useFormState } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Attachment,
  Documentation,
  FormTemplateQuestionFieldOption,
} from 'graphql-common';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import DocumentVersionNotes from '@lib/components/DocumentationPreview/DocumentVersionNotes';
import FileViewerModal from '@lib/components/FileViewerModal/FileViewerModal';
import getDocumentationFilePermissions from '@lib/utils/documentation/getDocumentationFilePermissions';
import _get from 'lodash/get';
import isDocOptionSatisfied from 'routes/Task/utils/isDocOptionSatisfied';
import DocumentQuestionOption from './DocumentQuestionOption';
import useDocumentationsByIds from './useDocumentationsByIds';
import useDocumentItemActions from './useDocumentItemActions';
import useFileViewerMarkAsRead from './useFileViewerMarkAsRead';
import { DocumentReadRecord } from './types';
import styles from './DocumentQuestionField.module.scss';

export type { DocumentReadRecord } from './types';

interface Props {
  fieldId: string;
  options: FormTemplateQuestionFieldOption[];
  readRecords: Record<string, DocumentReadRecord>;
  onMarkAsRead: (
    documentationId: string,
    versionId: string,
    versionViewId: string,
  ) => void;
  registerFieldDocs?: (
    fieldId: string,
    docs: Record<string, Documentation>,
  ) => void;
  disabled?: boolean;
}

/**
 * Renders a single DOCUMENT-kind question field — one row card per option.
 *
 * Fetches its own ≤5 documentations by id (admin caps DOCUMENT-field options
 * at 5 via `selectionLimit`); this avoids the 1000-row paginated-list limit
 * that a form-level fetch would hit on large templates. Resolved docs are
 * reported up through `registerFieldDocs(fieldId, map)` so the container can
 * assemble `docsByFieldId` and feed it to `getAnswers` at submit time.
 *
 * Read/unread state is local to this session via `readRecords`; a green
 * "Read" chip replaces the pink "Unread" chip once `onMarkAsRead` fires —
 * `FileViewerModal` only calls it after the view record is settled AND the
 * explicit `documentationVersionMarkAsRead` confirmation succeeds, so a BE
 * failure surfaces its own toast and leaves the modal open rather than
 * reaching this callback. The same `isDocOptionSatisfied`
 * check (BE-derived `doc.read` or an in-session record) also gates
 * `FileViewerModal`'s own "Mark as read" CTA, so reopening an
 * already-acknowledged document does not prompt to reconfirm it.
 *
 * When `disabled` is true (view route), the Mark-as-read affordance inside
 * `FileViewerModal` is hidden and the "Unread" chip is suppressed —
 * historical read state is not surfaced without BE data.
 */
export default function DocumentQuestionField(props: Props) {
  const {
    fieldId,
    options,
    readRecords,
    onMarkAsRead,
    registerFieldDocs,
    disabled,
  } = props;
  const { t } = useTranslation();
  // Subscribe to RHF's form state via the context's `control` so `isSubmitted`
  // reliably triggers a re-render when the user clicks Finish. Per-option
  // "Please read the document" rendering is gated on `isSubmitted` so the
  // usual submit-first flow is preserved.
  const { control } = useFormContext();
  const { isSubmitted } = useFormState({ control });

  const { docsById, isFirstLoading } = useDocumentationsByIds({
    fieldId,
    options,
    registerFieldDocs,
  });

  const {
    fileViewerDocument,
    openFileViewer,
    closeFileViewer,
    handleMarkAsRead,
  } = useFileViewerMarkAsRead({ onMarkAsRead });

  const { onToggleFavorite, onDetails } = useDocumentItemActions();

  const fileViewerPermissions =
    getDocumentationFilePermissions(fileViewerDocument);

  const [versionNotesDocument, setVersionNotesDocument] =
    useState<Documentation | null>(null);

  if (isFirstLoading) {
    return (
      <div className={styles.list}>
        <CircleLoader />
      </div>
    );
  }

  const sorted = [...options].sort((a, b) => a.position - b.position);

  return (
    <div className={styles.list}>
      {sorted.map((opt) => {
        const docId = opt.optionDocumentation?.documentationId;
        if (!docId) return null;
        return (
          <DocumentQuestionOption
            key={opt.id}
            option={opt}
            doc={docsById[docId]}
            readRecords={readRecords}
            isSubmitted={isSubmitted}
            disabled={disabled}
            onViewDocument={openFileViewer}
            onVersionNotes={setVersionNotesDocument}
            onToggleFavorite={onToggleFavorite}
            onDetails={onDetails}
            t={t}
          />
        );
      })}
      <FileViewerModal
        attachment={
          fileViewerDocument?.currentVersion?.attachmentAttached as
            Attachment | null | undefined
        }
        documentationVersionId={fileViewerDocument?.currentVersion?.id}
        isOpen={!!fileViewerDocument}
        onClose={closeFileViewer}
        useDownloadTool={fileViewerPermissions.allowDownload}
        usePrintTool={fileViewerPermissions.allowPrint}
        useMarkAsReadTool={!disabled}
        isMarkedAsRead={
          !!fileViewerDocument &&
          isDocOptionSatisfied(
            fileViewerDocument.id,
            fileViewerDocument,
            readRecords,
          )
        }
        onMarkAsRead={handleMarkAsRead}
      />
      <DocumentVersionNotes
        isOpen={!!versionNotesDocument}
        notes={_get(versionNotesDocument, ['currentVersion', 'notes']) || ''}
        onClose={() => setVersionNotesDocument(null)}
      />
    </div>
  );
}
